import { Injectable, OnDestroy } from '@angular/core';
import { IndexDbService } from './index-db.service';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { Collection } from '@app/_types/collection.type';
import { log } from '@app/_services/logger';
import { toTimestamp } from '@app/_services/date';
import { EntityRemovalNotification } from '@app/_interfaces/entity-removal-notification.interface';

/**
 * Centralized processor for EntityRemovalNotifications.
 *
 * Watches for EntityRemovalNotification changes and deletes matching entities from IndexedDB.
 * Stores don't need any filtering - they just subscribe to entity observables.
 *
 * Architecture: Same as Flutter EntityRemovalNotificationProcessor
 * - Subscribes to entityRemovalNotifications$ from IndexDbService
 * - Debounces to batch multiple activities (2 seconds)
 * - Checks timestamps before deleting (SA.createdAt > entity.cloudUpdatedAt)
 * - Deletes from local DB, letting reactive streams update UI
 *
 * See docs/DATA-SYNC.md for architecture details.
 */
@Injectable({ providedIn: 'root' })
export class EntityRemovalNotificationProcessorService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private isProcessing = false;
  private isStarted = false;

  constructor(private idb: IndexDbService) {}

  /**
   * Start processing EntityRemovalNotifications.
   * Call this once during app initialization.
   */
  start(): void {
    if (this.isStarted) {
      log.warn('EntityRemovalNotificationProcessor', 'Already started, ignoring duplicate start');
      return;
    }

    this.isStarted = true;
    log.debug('EntityRemovalNotificationProcessor', 'Started');

    this.idb.entityRemovalNotifications$
      .pipe(
        debounceTime(2000), // Batch multiple activities
        takeUntil(this.destroy$)
      )
      .subscribe(activities => {
        if (activities && activities.length > 0) {
          this.processActivities(activities);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async processActivities(activities: EntityRemovalNotification[]): Promise<void> {
    if (!activities || activities.length === 0) return;
    if (this.isProcessing) {
      log.warn('EntityRemovalNotificationProcessor', 'Already processing, skipping');
      return;
    }

    this.isProcessing = true;

    try {
      for (const activity of activities) {
        await this.processActivity(activity);
      }
    } catch (error) {
      log.error('EntityRemovalNotificationProcessor', 'Error processing', error as Error);
    } finally {
      this.isProcessing = false;
    }
  }

  private async processActivity(activity: EntityRemovalNotification): Promise<void> {
    if (activity.actionType !== 'remove') return;

    const collection = activity.targetEntityType as Collection;
    if (!collection) {
      log.warn('EntityRemovalNotificationProcessor', 'No targetEntityType in activity');
      return;
    }

    // Handle integration-based cleanup (v22+)
    if (activity.clearAllForIntegration && activity.calendarIntegrationId) {
      await this.processIntegrationCleanup(activity, collection);
      return;
    }

    const entityIds = activity.entityIds;
    if (!entityIds || entityIds.length === 0) return;

    try {
      const activityCreatedAtMs = toTimestamp(activity.createdAt);
      const idsToDelete: string[] = [];

      for (const entityId of entityIds) {
        try {
          const entity = await this.idb.getByKey(collection, entityId);
          const entityCloudUpdatedAtMs = entity ? toTimestamp(entity.cloudUpdatedAt) : 0;

          if (entity && activityCreatedAtMs > entityCloudUpdatedAtMs) {
            idsToDelete.push(entityId);
          }
        } catch {
          // Entity not found, skip
        }
      }

      if (idsToDelete.length > 0) {
        log.info('EntityRemovalNotificationProcessor',
          `Deleting ${idsToDelete.length} from ${collection}`);
        await this.idb.bulkDelete(collection, idsToDelete);
      }
    } catch (error) {
      log.error('EntityRemovalNotificationProcessor', `Error processing ${collection}`, error as Error);
    }
  }

  /**
   * Handle integration-based cleanup (v22+).
   * Used when sync timed out and no events exist in Firestore,
   * but client may have events from a previous sync that need cleanup.
   */
  private async processIntegrationCleanup(
    activity: EntityRemovalNotification,
    collection: Collection
  ): Promise<void> {
    if (collection !== 'calendarEventSetups' && collection !== 'calendarEventActions') {
      log.warn('EntityRemovalNotificationProcessor', `Unsupported collection for integration cleanup: ${collection}`);
      return;
    }

    const integrationId = activity.calendarIntegrationId!;
    const deletedAtMs = toTimestamp(activity.integrationDeletedAt) || toTimestamp(activity.createdAt);

    try {
      const result = await this.idb.getByIndex(collection, 'calendarIntegrationId', integrationId);
      const events = Array.isArray(result) ? result : [result];

      if (!events || events.length === 0 || !events[0]) {
        return;
      }

      const validEvents = events.filter(e => e && e.id);
      const idsToDelete: string[] = [];

      for (const event of validEvents) {
        const entityCloudUpdatedAtMs = toTimestamp(event.cloudUpdatedAt);
        if (deletedAtMs > entityCloudUpdatedAtMs) {
          idsToDelete.push(event.id);
        }
      }

      if (idsToDelete.length > 0) {
        log.info('EntityRemovalNotificationProcessor',
          `Integration cleanup: Deleting ${idsToDelete.length} from ${collection}`);
        await this.idb.bulkDelete(collection, idsToDelete);
      }
    } catch (error) {
      log.error('EntityRemovalNotificationProcessor', `Error in integration cleanup for ${collection}`, error as Error);
    }
  }
}
